import Head from 'next/head'

import type { FC, ReactNode } from 'react'
import type { JsonLd, SeoProps } from '@/types'

import { absoluteUrl } from '@/lib/structured-data'
import { siteConfig } from '@/constants/site'

const serialize = (data: JsonLd): string => JSON.stringify(data).replace(/</g, '\\u003c')

export const Seo: FC<SeoProps> = ({
    title,
    description = siteConfig.description,
    ogImage = siteConfig.ogImage,
    path = '/',
    noindex = false,
    jsonLd
}): ReactNode => {
    const pageTitle = title ? `${title} - ${siteConfig.name}` : `${siteConfig.name} - ${siteConfig.tagline}`
    const canonical = absoluteUrl(path)
    const schemas = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : []

    return (
        <Head>
            <title>
                {pageTitle}
            </title>

            <meta key='description' name='description' content={description} />

            {noindex
                ? <meta key='robots' name='robots' content='noindex, follow' />
                : <link key='canonical' rel='canonical' href={canonical} />}

            <meta key='og:site_name' property='og:site_name' content={siteConfig.name} />
            <meta key='og:locale' property='og:locale' content='en_IN' />
            <meta key='og:title' property='og:title' content={pageTitle} />
            <meta key='og:description' property='og:description' content={description} />
            <meta key='og:type' property='og:type' content='website' />
            <meta key='og:url' property='og:url' content={canonical} />
            <meta key='og:image' property='og:image' content={ogImage} />
            <meta key='twitter:card' name='twitter:card' content='summary_large_image' />
            <meta key='twitter:title' name='twitter:title' content={pageTitle} />
            <meta key='twitter:description' name='twitter:description' content={description} />
            <meta key='twitter:image' name='twitter:image' content={ogImage} />

            {schemas.map((schema, index) => (
                <script
                    key={`json-ld-${index}`}
                    type='application/ld+json'
                    dangerouslySetInnerHTML={{ __html: serialize(schema) }}
                />
            ))}
        </Head>
    )
}
